import { createModel } from '@rematch/core';
import Axios from 'axios';
import { Term } from './paymentTerms';

export type FortisOpenParams = {
  total: number;
  items: FortisItem[];
  orderId: string | null;
  orderDesc: string | null;
  tax: number | null;
  shipping: number | null;
};

export type FortisItem = {
  itemId: string;
  itemDescription: string;
  itemPrice: number;
  itemQty: number;
  um: string | 'each';
  productCode: string;
};

export interface FortisResponse {
  data: [
    {
      txnId: string;
      payment: {
        method: number;
      };
    }
  ];
}

export type GetConfigSettings = {
  dealerId: string;
  FortisSuccess: (responseData: FortisResponse[]) => void;
  FortisFailure: () => void;
};

type PaymentStatus = {
  quoteId: string;
  paymentStatus: string;
  transactionId: string | null;
  paymentMethod: number;
  selectedTerms?: number[];
  companyId: string;
};

type PaymentMethods = {
  CreditCard: boolean;
  BankWire: boolean;
  ExpressFinancing: boolean;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Window {
    FortisWindow: {
      config: FortisConfig;
      destroy?: () => void;
      open: (params: FortisOpenParams) => void;
      onSuccess: () => void;
      onFailure: () => void;
      onUnderReview: () => void;
      onInit: () => void;
    };
  }
}

interface DealerAPIFortisData {
  developerId: string;
  userId: string;
  userApiKey: string;
  locationId: string;
  cCProductTransactionId: string;
  aCHProductTransactionId: string;
  paymentMethods: {
    AllowCreditCard: boolean;
    AllowBankWire: boolean;
    AllowExpressFinancing: boolean;
  };
}

type FortisConfig = {
  developerId: string;
  userId: string;
  userApiKey: string;
  locationId: string;
  cCProductTransactionId: string;
  aCHProductTransactionId: string;
  onSuccess?: () => void;
  onFailure?: () => void;
  onUnderReview?: () => void;
  onInit?: () => void;
  paymentMethods: {
    CreditCard: boolean;
    BankWire: boolean;
    ExpressFinancing: boolean;
  };
};

type FortisState = {
  config: FortisConfig;
  isLoaded: boolean;
  scriptIsLoaded: boolean;
  isInitialized: boolean;
  isDefaultAccount: boolean;
};

const defaultState: FortisState = {
  config: {
    developerId: '',
    userId: '',
    userApiKey: '',
    locationId: '',
    cCProductTransactionId: '',
    aCHProductTransactionId: '',
    paymentMethods: {
      CreditCard: false,
      BankWire: false,
      ExpressFinancing: false
    }
  },
  isLoaded: false,
  scriptIsLoaded: false,
  isInitialized: false,
  isDefaultAccount: false
};

const setFortisConfig = function handleSetFortisConfig(
  state: FortisState,
  apiData: DealerAPIFortisData
): FortisState {
  const config = {
    ...state.config,
    developerId: apiData.developerId,
    financingEnabled: false,
    userId: apiData.userId,
    userApiKey: apiData.userApiKey,
    locationId: '',
    cCProductTransactionId: apiData.cCProductTransactionId,
    aCHProductTransactionId: apiData.aCHProductTransactionId,
    paymentMethods: state.config.paymentMethods
  };

  return {
    ...state,
    config
  };
};

const setFortisIsLoaded = function handleSetFortisConfig(
  state: FortisState,
  isLoaded: boolean
): FortisState {
  return {
    ...state,
    isLoaded
  };
};

const setIsDefaultAccount = function handleIsDefaultAccount(
  state: FortisState,
  isDefaultAccount: boolean
): FortisState {
  return {
    ...state,
    isDefaultAccount
  };
};

const scriptIsLoaded = function setScriptIsLoaded(
  state: FortisState
): FortisState {
  return {
    ...state,
    scriptIsLoaded: true
  };
};

const isInitialized = function setIsInitialized(
  state: FortisState
): FortisState {
  return {
    ...state,
    isInitialized: true
  };
};

const setPaymentMethods = function handleSetPaymentMethods(
  state: FortisState,
  paymentMethods: PaymentMethods
): FortisState {
  return {
    ...state,
    config: {
      ...state.config,
      paymentMethods
    }
  };
};

type FortisReinitialize = {
  onSuccess: () => void;
  onFailure: () => void;
  onUnderReview: () => void;
  onInit: () => void;
};

const reinitialize = function reinitializeFortisWidget(
  state: FortisState,
  initialize: FortisReinitialize
): FortisState {
  if (window.FortisWindow.destroy) {
    window.FortisWindow.destroy();
  }

  const onSuccess = initialize.onSuccess || state.config.onSuccess;
  const onFailure = initialize.onFailure || state.config.onFailure;
  const onUnderReview = initialize.onUnderReview || state.config.onUnderReview;
  const onInit = initialize.onInit || state.config.onInit;
  const Fortis = {
    config: state.config,
    open: window.FortisWindow && window.FortisWindow.open,
    onSuccess,
    onFailure,
    onUnderReview,
    onInit
  };
  window.FortisWindow = Fortis;

  const event = new CustomEvent('initializeWidget');
  window.document.dispatchEvent(event);

  return {
    ...state,
    isInitialized: false,
    config: { ...state.config, onSuccess, onFailure, onUnderReview, onInit }
  };
};
const resetFinancing = function resetFinancingFromApiDefault(
  state: FortisState
): FortisState {
  return {
    ...state,
    config: {
      ...state.config,
      paymentMethods: {
        CreditCard: state.config.paymentMethods.CreditCard,
        BankWire: state.config.paymentMethods.BankWire,
        ExpressFinancing: false
      }
    }
  };
};

const fortis = createModel({
  state: defaultState,
  reducers: {
    setFortisConfig,
    setFortisIsLoaded,
    setIsDefaultAccount,
    scriptIsLoaded,
    isInitialized,
    reinitialize,
    resetFinancing,
    setPaymentMethods
  },
  effects: {
    async getConfig(dealerId: string): Promise<void> {
      try {
        const dealerApi = Axios.create({
          baseURL: window.AutoQuotes.config.apiBaseUrl
        });

        const { data: apiData } = await dealerApi.get(`fortis/${dealerId}/pay`);

        this.setFortisConfig(apiData);

        if (apiData.publicApiKey) {
          this.setFortisIsLoaded(true);
          this.setIsDefaultAccount(apiData.isDefaultAccount);
        }
      } catch (err) {
        this.setFortisIsLoaded(false);
      }
    },
    async savePaymentStatus(payment: PaymentStatus): Promise<Term[]> {
      const {
        quoteId,
        paymentStatus,
        transactionId,
        paymentMethod,
        selectedTerms,
        companyId
      } = payment;

      const paymentStatusApi = Axios.create({
        baseURL: window.AutoQuotes.config.apiBaseUrl
      });

      const { data } = await paymentStatusApi.post(`quote/${quoteId}/payment`, {
        paymentStatus,
        transactionId,
        paymentMethod,
        selectedTerms,
        companyId
      });

      return data;
    }
  }
});

export default fortis;
